import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  BookOpen, 
  Plus, 
  Edit, 
  Trash2, 
  ArrowLeft, 
  ChevronDown, 
  ChevronRight,
  Image,
  FileText,
  Users,
  BookMarked,
  PenTool,
  Save,
  Check,
  Square,
  CheckSquare,
  X,
  ExternalLink,
  UserPlus,
  Copy,
  Mail,
  Crown,
  GripVertical,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Sidebar from '../components/Sidebar';
import { PostItIcon } from '../components/PostItIcon';
import { toast } from 'sonner';
import { AuthorshipPanel, AuthorshipCertificate, CoauthorDiagnostic } from '../components/coautoria';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Roman numerals helper
const toRoman = (num) => {
  const romanNumerals = [
    ['M', 1000], ['CM', 900], ['D', 500], ['CD', 400],
    ['C', 100], ['XC', 90], ['L', 50], ['XL', 40],
    ['X', 10], ['IX', 9], ['V', 5], ['IV', 4], ['I', 1]
  ];
  let result = '';
  for (const [letter, value] of romanNumerals) {
    while (num >= value) {
      result += letter;
      num -= value;
    }
  }
  return result;
};

// Planning checklist default items
const defaultPlanningItems = [
  { key: 'resumo', label: 'Resumo da obra', placeholder: 'Descreva brevemente do que trata sua história...', type: 'textarea' },
  { key: 'tema', label: 'Tema central', placeholder: 'Qual é o tema principal que permeia sua narrativa?', type: 'textarea' },
  { key: 'conflito', label: 'Conflito principal', placeholder: 'Qual conflito move a história?', type: 'textarea' },
  { key: 'personagem', label: 'Personagem principal', placeholder: 'Quem é o protagonista e qual sua jornada?', type: 'textarea' },
  { key: 'final', label: 'Final da história', placeholder: 'Como você imagina o desfecho?', type: 'textarea' },
  { key: 'paginas_estimadas', label: 'Quantas páginas você imagina que seu livro terá?', placeholder: 'Ex: 200', type: 'number' }
];

// Book format specifications for progress calculation
const bookFormats = {
  pocket: { wordsPerPage: 250, avgWords: 40000 },
  kindle: { wordsPerPage: 280, avgWords: 70000 }
};

export default function LivroEstrutura() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [parts, setParts] = useState([]);
  const [subchapters, setSubchapters] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Helper to get saved section from localStorage (valid for 3 hours)
  const getSavedSection = () => {
    try {
      const saved = localStorage.getItem(`livro_${bookId}_lastSection`);
      if (saved) {
        const { section, timestamp } = JSON.parse(saved);
        const threeHours = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
        if (Date.now() - timestamp < threeHours) {
          return section;
        }
      }
    } catch (e) {
      // localStorage may fail in private mode - log but don't crash
      console.debug('[LivroEstrutura] localStorage read error:', e.message);
    }
    return null;
  };
  
  // Section expansion state - restore last opened section if valid
  const [expandedSections, setExpandedSections] = useState(() => {
    const savedSection = getSavedSection();
    return {
      identidade: savedSection === 'identidade',
      planejamento: savedSection === 'planejamento',
      sumario: savedSection === 'sumario',
      autoria: savedSection === 'autoria',
      encerramento: savedSection === 'encerramento'
    };
  });
  
  // Certificate modal state
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  
  // Planning fields state
  const [planningData, setPlanningData] = useState({
    resumo: { text: '', completed: false },
    tema: { text: '', completed: false },
    conflito: { text: '', completed: false },
    personagem: { text: '', completed: false },
    final: { text: '', completed: false },
    paginas_estimadas: { text: '', completed: false }
  });
  
  // Encerramento fields state
  const [encerramentoData, setEncerramentoData] = useState({
    dedication: '',
    preface: '',
    acknowledgments: '',
    about_author: ''
  });
  
  // Editing state
  const [editingField, setEditingField] = useState(null);
  const [savingField, setSavingField] = useState(null);
  
  // New item inputs
  const [newPartTitle, setNewPartTitle] = useState('');
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newSubchapterTitle, setNewSubchapterTitle] = useState('');
  const [addingPart, setAddingPart] = useState(false);
  const [addingChapter, setAddingChapter] = useState(null); // null, 'selecting', or part_id
  const [addingSubchapter, setAddingSubchapter] = useState(null); // null, 'selecting', or chapter_id
  
  // Inline editing state for sumario items
  const [editingPartId, setEditingPartId] = useState(null);
  const [editingChapterId, setEditingChapterId] = useState(null);
  const [editingSubchapterId, setEditingSubchapterId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  
  // Drag and drop state
  const [draggedChapter, setDraggedChapter] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  
  // Synopsis/Note editing state
  const [editingSynopsisId, setEditingSynopsisId] = useState(null);
  const [synopsisText, setSynopsisText] = useState('');
  
  // Colegas Escritores state
  const [colleagues, setColleagues] = useState({ owner: null, colleagues: [], pending_invites: [], is_owner: false });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLink, setInviteLink] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);

  // Calculate book progress
  const calculateProgress = () => {
    const totalWords = book?.total_words || 0;
    const totalChapters = chapters.length;
    const chaptersStarted = chapters.filter(ch => (ch.word_count || 0) > 0).length;
    
    // Priority 1: Use chapters from sumário (only if 5+ chapters)
    if (totalChapters >= 5) {
      const percentage = Math.min(Math.round((chaptersStarted / totalChapters) * 100), 100);
      return {
        percentage,
        type: 'chapters',
        label: `${chaptersStarted} de ${totalChapters} capítulos iniciados`,
        totalWords,
        isEstimate: false
      };
    }
    
    // Priority 2: Use estimated pages from planning
    const estimatedPages = parseInt(planningData.paginas_estimadas?.text) || 0;
    if (estimatedPages > 0) {
      const format = book?.size || 'kindle';
      const wordsPerPage = bookFormats[format]?.wordsPerPage || 280;
      const targetWords = estimatedPages * wordsPerPage;
      const percentage = Math.min(Math.round((totalWords / targetWords) * 100), 100);
      return {
        percentage,
        type: 'pages',
        label: `${totalWords.toLocaleString('pt-BR')} de ~${targetWords.toLocaleString('pt-BR')} palavras`,
        totalWords,
        isEstimate: true,
        estimateSource: `${estimatedPages} páginas estimadas`
      };
    }
    
    // Priority 3: Use book format average
    const format = book?.size || 'kindle';
    const targetWords = bookFormats[format]?.avgWords || 70000;
    const percentage = Math.min(Math.round((totalWords / targetWords) * 100), 100);
    return {
      percentage,
      type: 'format',
      label: `${totalWords.toLocaleString('pt-BR')} palavras`,
      totalWords,
      isEstimate: true,
      estimateSource: `média editorial ${format === 'pocket' ? 'Pocket' : 'Kindle'}`
    };
  };

  const progress = book ? calculateProgress() : { percentage: 0, totalWords: 0 };

  // Fetch book data (declared before useEffect)
  const fetchBookData = useCallback(async () => {
    try {
      const [bookRes, chaptersRes, partsRes] = await Promise.all([
        axios.get(`${API_URL}/api/books/${bookId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/books/${bookId}/chapters`, { withCredentials: true }),
        axios.get(`${API_URL}/api/books/${bookId}/parts`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      const bookData = bookRes.data;
      setBook(bookData);
      setChapters(chaptersRes.data);
      setParts(partsRes.data);
      
      // Initialize encerramento data from book
      setEncerramentoData({
        dedication: bookData.dedication || '',
        preface: bookData.preface || '',
        acknowledgments: bookData.acknowledgments || '',
        about_author: bookData.about_author || ''
      });
      
      // Initialize planning data from book_summary (stored as JSON)
      if (bookData.book_summary) {
        try {
          const parsed = JSON.parse(bookData.book_summary);
          setPlanningData(parsed);
        } catch {
          // If not JSON, use as resumo text
          setPlanningData(prev => ({
            ...prev,
            resumo: { text: bookData.book_summary, completed: !!bookData.book_summary }
          }));
        }
      }

      // Fetch subchapters for each chapter
      const subchapterPromises = chaptersRes.data.map(ch =>
        axios.get(`${API_URL}/api/chapters/${ch.chapter_id}/subchapters`, { withCredentials: true })
          .then(res => ({ chapterId: ch.chapter_id, subchapters: res.data }))
          .catch(() => ({ chapterId: ch.chapter_id, subchapters: [] }))
      );
      
      const subchapterResults = await Promise.all(subchapterPromises);
      const subchapterMap = {};
      subchapterResults.forEach(({ chapterId, subchapters }) => {
        subchapterMap[chapterId] = subchapters;
      });
      setSubchapters(subchapterMap);
      
    } catch (error) {
      console.error('Error fetching book data:', error);
      toast.error('Erro ao carregar livro');
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Fetch colleagues (declared before useEffect)
  const fetchColleagues = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/books/${bookId}/colleagues`, { withCredentials: true });
      setColleagues(response.data);
    } catch (error) {
      console.error('Error fetching colleagues:', error);
    }
  }, [bookId]);

  // Load data on mount
  useEffect(() => {
    fetchBookData();
    fetchColleagues();
  }, [bookId, fetchBookData, fetchColleagues]);

  // Auto-refresh colleagues list when modal is open (polling every 30s)
  useEffect(() => {
    if (!showInviteModal) return;
    
    const interval = setInterval(() => {
      fetchColleagues();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [showInviteModal, fetchColleagues]);

  // Send invite
  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Digite o email do colega escritor');
      return;
    }
    
    setSendingInvite(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/books/${bookId}/invite`,
        { email: inviteEmail.trim() },
        { withCredentials: true }
      );
      
      const fullLink = `${window.location.origin}${response.data.invite_link}`;
      setInviteLink(fullLink);
      toast.success('Convite criado! Copie o link e envie para seu colega.');
      fetchColleagues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar convite');
    } finally {
      setSendingInvite(false);
    }
  };

  // Copy invite link
  const copyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      toast.success('Link copiado!');
    }
  };

  // Remove colleague
  const handleRemoveColleague = async (userId) => {
    if (!window.confirm('Deseja desfazer essa parceria?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/books/${bookId}/colleagues/${userId}`, { withCredentials: true });
      toast.success('Parceria desfeita');
      fetchColleagues();
    } catch (error) {
      toast.error('Erro ao remover colega');
    }
  };

  // Cancel invite
  const handleCancelInvite = async (inviteId) => {
    try {
      await axios.delete(`${API_URL}/api/books/${bookId}/invites/${inviteId}`, { withCredentials: true });
      toast.success('Convite cancelado');
      fetchColleagues();
    } catch (error) {
      toast.error('Erro ao cancelar convite');
    }
  };

  // Open invite modal with fresh data
  const handleOpenInviteModal = () => {
    fetchColleagues(); // Refresh data when opening
    setShowInviteModal(true);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const isCurrentlyOpen = prev[section];
      const willBeOpen = !isCurrentlyOpen;
      
      // Save to localStorage if opening a section (renews the 3-hour timer)
      if (willBeOpen) {
        try {
          localStorage.setItem(`livro_${bookId}_lastSection`, JSON.stringify({
            section,
            timestamp: Date.now()
          }));
        } catch (e) {
          // localStorage may fail in private mode - log but don't crash
          console.debug('[LivroEstrutura] localStorage write error:', e.message);
        }
      }
      
      // Close all sections first, then open only the clicked one (if it was closed)
      return {
        identidade: false,
        planejamento: false,
        sumario: false,
        encerramento: false,
        [section]: willBeOpen
      };
    });
  };

  // Save planning data to backend
  const savePlanningData = async (newData) => {
    try {
      await axios.put(
        `${API_URL}/api/books/${bookId}`,
        { book_summary: JSON.stringify(newData) },
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Error saving planning data:', error);
    }
  };

  const handlePlanningToggle = async (key) => {
    const newData = {
      ...planningData,
      [key]: { ...planningData[key], completed: !planningData[key].completed }
    };
    setPlanningData(newData);
    await savePlanningData(newData);
  };

  const handlePlanningTextChange = (key, text) => {
    setPlanningData(prev => ({
      ...prev,
      [key]: { ...prev[key], text }
    }));
  };

  const handlePlanningTextSave = async (key) => {
    const newData = { ...planningData };
    await savePlanningData(newData);
    setEditingField(null);
    toast.success('Salvo!');
  };

  // Save encerramento fields
  const handleSaveEncerramentoField = async (fieldName) => {
    setSavingField(fieldName);
    try {
      await axios.put(
        `${API_URL}/api/books/${bookId}`,
        { [fieldName]: encerramentoData[fieldName] },
        { withCredentials: true }
      );
      setBook(prev => ({ ...prev, [fieldName]: encerramentoData[fieldName] }));
      setEditingField(null);
      toast.success('Salvo com sucesso!');
    } catch (error) {
      console.error('Error saving field:', error);
      toast.error('Erro ao salvar');
    } finally {
      setSavingField(null);
    }
  };

  // Part management
  const handleCreatePart = async () => {
    if (!newPartTitle.trim()) {
      toast.error('Digite um título para a parte');
      return;
    }
    
    try {
      const newOrder = parts.length + 1;
      await axios.post(
        `${API_URL}/api/books/${bookId}/parts`,
        { title: newPartTitle.trim(), order: newOrder },
        { withCredentials: true }
      );
      
      toast.success('Parte criada!');
      setNewPartTitle('');
      setAddingPart(false);
      fetchBookData();
    } catch (error) {
      console.error('Error creating part:', error);
      toast.error('Erro ao criar parte');
    }
  };

  const handleDeletePart = async (partId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta parte?')) return;

    try {
      await axios.delete(`${API_URL}/api/parts/${partId}`, { withCredentials: true });
      toast.success('Parte excluída');
      fetchBookData();
    } catch (error) {
      console.error('Error deleting part:', error);
      toast.error('Erro ao excluir parte');
    }
  };

  // Chapter management
  const handleCreateChapter = async (partId = null) => {
    const title = newChapterTitle.trim() || `Capítulo ${chapters.length + 1}`;
    
    try {
      // Calculate the highest order among all chapters and add 1
      const maxOrder = chapters.reduce((max, ch) => Math.max(max, ch.order || 0), 0);
      const newOrder = maxOrder + 1;
      
      await axios.post(
        `${API_URL}/api/books/${bookId}/chapters`,
        {
          title,
          content: '',
          order: newOrder,
          part_id: partId
        },
        { withCredentials: true }
      );
      
      toast.success('Capítulo criado!');
      setNewChapterTitle('');
      setAddingChapter(null);
      fetchBookData();
    } catch (error) {
      console.error('Error creating chapter:', error);
      toast.error('Erro ao criar capítulo');
    }
  };

  const handleDeleteChapter = async (chapterId) => {
    if (!window.confirm('Tem certeza que deseja excluir este capítulo?')) return;

    try {
      await axios.delete(`${API_URL}/api/chapters/${chapterId}`, { withCredentials: true });
      toast.success('Capítulo excluído');
      fetchBookData();
    } catch (error) {
      console.error('Error deleting chapter:', error);
      toast.error('Erro ao excluir capítulo');
    }
  };

  // Subchapter management
  const handleCreateSubchapter = async (chapterId) => {
    try {
      const existingSubchapters = subchapters[chapterId] || [];
      const chapter = chapters.find(c => c.chapter_id === chapterId);
      const chapterNum = chapters.indexOf(chapter) + 1;
      const newOrder = existingSubchapters.length + 1;
      
      await axios.post(
        `${API_URL}/api/chapters/${chapterId}/subchapters`,
        {
          chapter_id: chapterId,
          title: `${chapterNum}.${newOrder} Subcapítulo`,
          content: '',
          order: newOrder
        },
        { withCredentials: true }
      );
      
      toast.success('Subcapítulo criado!');
      setAddingSubchapter(null);
      fetchBookData();
    } catch (error) {
      console.error('Error creating subchapter:', error);
      toast.error('Erro ao criar subcapítulo');
    }
  };

  // Create subchapter with custom title from input
  const handleCreateSubchapterWithTitle = async (chapterId) => {
    if (!newSubchapterTitle.trim()) {
      toast.error('Digite um título para o subcapítulo');
      return;
    }
    try {
      const existingSubchapters = subchapters[chapterId] || [];
      const newOrder = existingSubchapters.length + 1;
      
      await axios.post(
        `${API_URL}/api/chapters/${chapterId}/subchapters`,
        {
          chapter_id: chapterId,
          title: newSubchapterTitle.trim(),
          content: '',
          order: newOrder
        },
        { withCredentials: true }
      );
      
      toast.success('Subcapítulo criado!');
      setNewSubchapterTitle('');
      setAddingSubchapter(null);
      fetchBookData();
    } catch (error) {
      console.error('Error creating subchapter:', error);
      toast.error('Erro ao criar subcapítulo');
    }
  };

  const handleDeleteSubchapter = async (subchapterId) => {
    if (!window.confirm('Tem certeza que deseja excluir este subcapítulo?')) return;

    try {
      await axios.delete(`${API_URL}/api/subchapters/${subchapterId}`, { withCredentials: true });
      toast.success('Subcapítulo excluído');
      fetchBookData();
    } catch (error) {
      console.error('Error deleting subchapter:', error);
      toast.error('Erro ao excluir subcapítulo');
    }
  };

  // Inline title editing functions
  const handleEditPartTitle = async (partId) => {
    if (!editTitle.trim()) return;
    try {
      await axios.put(`${API_URL}/api/parts/${partId}`, { title: editTitle.trim() }, { withCredentials: true });
      toast.success('Parte atualizada!');
      setEditingPartId(null);
      setEditTitle('');
      fetchBookData();
    } catch (error) {
      console.error('Error updating part:', error);
      toast.error('Erro ao atualizar parte');
    }
  };

  const handleEditChapterTitle = async (chapterId) => {
    if (!editTitle.trim()) return;
    try {
      await axios.put(`${API_URL}/api/chapters/${chapterId}`, { title: editTitle.trim() }, { withCredentials: true });
      toast.success('Capítulo atualizado!');
      setEditingChapterId(null);
      setEditTitle('');
      fetchBookData();
    } catch (error) {
      console.error('Error updating chapter:', error);
      toast.error('Erro ao atualizar capítulo');
    }
  };

  const handleEditSubchapterTitle = async (subchapterId) => {
    if (!editTitle.trim()) return;
    try {
      await axios.put(`${API_URL}/api/subchapters/${subchapterId}`, { title: editTitle.trim() }, { withCredentials: true });
      toast.success('Subcapítulo atualizado!');
      setEditingSubchapterId(null);
      setEditTitle('');
      fetchBookData();
    } catch (error) {
      console.error('Error updating subchapter:', error);
      toast.error('Erro ao atualizar subcapítulo');
    }
  };

  const startEditingPart = (part) => {
    setEditingPartId(part.part_id);
    setEditTitle(part.title);
    setEditingChapterId(null);
    setEditingSubchapterId(null);
  };

  const startEditingChapter = (chapter) => {
    setEditingChapterId(chapter.chapter_id);
    setEditTitle(chapter.title);
    setEditingPartId(null);
    setEditingSubchapterId(null);
  };

  const startEditingSubchapter = (subchapter) => {
    setEditingSubchapterId(subchapter.subchapter_id);
    setEditTitle(subchapter.title);
    setEditingPartId(null);
    setEditingChapterId(null);
  };

  const cancelEditing = () => {
    setEditingPartId(null);
    setEditingChapterId(null);
    setEditingSubchapterId(null);
    setEditTitle('');
  };

  // Synopsis/Note functions
  const startEditingSynopsis = (chapter) => {
    setEditingSynopsisId(chapter.chapter_id);
    setSynopsisText(chapter.synopsis || '');
  };

  const handleSaveSynopsis = async (chapterId) => {
    try {
      await axios.put(
        `${API_URL}/api/chapters/${chapterId}`, 
        { synopsis: synopsisText.trim() || null }, 
        { withCredentials: true }
      );
      toast.success('Síntese salva!');
      setEditingSynopsisId(null);
      setSynopsisText('');
      fetchBookData();
    } catch (error) {
      console.error('Error saving synopsis:', error);
      toast.error('Erro ao salvar síntese');
    }
  };

  const cancelSynopsisEditing = () => {
    setEditingSynopsisId(null);
    setSynopsisText('');
  };

  // Drag and Drop functions for chapters
  const handleDragStart = (e, chapter) => {
    setDraggedChapter(chapter);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', chapter.chapter_id);
    // Add visual feedback
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedChapter(null);
    setDragOverTarget(null);
  };

  const handleDragOver = (e, targetChapterId, targetPartId = null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ chapterId: targetChapterId, partId: targetPartId });
  };

  const handleDragLeave = () => {
    setDragOverTarget(null);
  };

  const handleDrop = async (e, targetChapter, targetPartId = null) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);
    
    if (!draggedChapter) {
      return;
    }
    
    // Don't do anything if dropping on itself
    if (targetChapter && draggedChapter.chapter_id === targetChapter.chapter_id) {
      setDraggedChapter(null);
      return;
    }

    try {
      // Create a copy of chapters array
      const updatedChapters = [...chapters];
      
      // Find the dragged chapter index
      const draggedIndex = updatedChapters.findIndex(c => c.chapter_id === draggedChapter.chapter_id);
      if (draggedIndex === -1) {
        setDraggedChapter(null);
        return;
      }
      
      // Find target index
      let targetIndex = targetChapter 
        ? updatedChapters.findIndex(c => c.chapter_id === targetChapter.chapter_id)
        : updatedChapters.length;
      
      // Remove dragged chapter from its original position
      const [movedChapter] = updatedChapters.splice(draggedIndex, 1);
      
      // Update part_id for the moved chapter (use target chapter's part if available)
      movedChapter.part_id = targetPartId !== undefined ? targetPartId : (targetChapter?.part_id || null);
      
      // Recalculate target index after removal (if dragged was before target)
      if (draggedIndex < targetIndex) {
        targetIndex -= 1;
      }
      
      // Insert AFTER the target chapter (targetIndex + 1), not before
      // This makes it more intuitive - drop on a chapter = insert after it
      updatedChapters.splice(targetIndex + 1, 0, movedChapter);
      
      // Prepare bulk update data with new orders
      const reorderData = updatedChapters.map((ch, idx) => ({
        chapter_id: ch.chapter_id,
        order: idx + 1,
        part_id: ch.part_id
      }));

      // Send bulk reorder request FIRST
      await axios.put(
        `${API_URL}/api/books/${bookId}/chapters/reorder`,
        reorderData,
        { withCredentials: true }
      );

      // Update local state AFTER successful save
      setChapters(updatedChapters);
      toast.success('Capítulo reorganizado!');
    } catch (error) {
      console.error('Error reordering chapter:', error);
      toast.error('Erro ao reorganizar capítulo');
      // Refresh data on error
      fetchBookData();
    } finally {
      setDraggedChapter(null);
    }
  };

  // Simple function to move a chapter to a part (called from drop on Part header)
  const handleMoveChapterToPart = async (chapter, targetPartId) => {
    if (!chapter || chapter.part_id === targetPartId) {
      setDraggedChapter(null);
      return;
    }

    try {
      // Create a copy of chapters array
      const updatedChapters = [...chapters];
      
      // Find and remove dragged chapter
      const draggedIndex = updatedChapters.findIndex(c => c.chapter_id === chapter.chapter_id);
      if (draggedIndex === -1) {
        setDraggedChapter(null);
        return;
      }
      
      const [movedChapter] = updatedChapters.splice(draggedIndex, 1);
      
      // Update part_id
      movedChapter.part_id = targetPartId;
      
      // Find the last chapter in this part to insert after it
      const partChapters = updatedChapters.filter(ch => ch.part_id === targetPartId);
      let insertIndex;
      
      if (partChapters.length === 0) {
        // No chapters in part yet
        // Find where this part is in the order of parts, and insert at appropriate position
        const targetPartIndex = parts.findIndex(p => p.part_id === targetPartId);
        
        // Count all chapters that belong to parts BEFORE this one
        let countBefore = 0;
        for (let i = 0; i < updatedChapters.length; i++) {
          const ch = updatedChapters[i];
          if (ch.part_id) {
            const chPartIndex = parts.findIndex(p => p.part_id === ch.part_id);
            if (chPartIndex < targetPartIndex) {
              countBefore = i + 1; // Insert after this chapter
            }
          }
        }
        insertIndex = countBefore;
      } else {
        // Insert after the last chapter in this part
        const lastPartChapter = partChapters[partChapters.length - 1];
        insertIndex = updatedChapters.findIndex(c => c.chapter_id === lastPartChapter.chapter_id) + 1;
      }
      
      // Insert chapter at new position
      updatedChapters.splice(insertIndex, 0, movedChapter);
      
      // Prepare bulk update data
      const reorderData = updatedChapters.map((ch, idx) => ({
        chapter_id: ch.chapter_id,
        order: idx + 1,
        part_id: ch.part_id
      }));

      // Send bulk reorder request
      await axios.put(
        `${API_URL}/api/books/${bookId}/chapters/reorder`,
        reorderData,
        { withCredentials: true }
      );

      // Update local state
      setChapters(updatedChapters);
      toast.success('Capítulo movido para a parte!');
    } catch (error) {
      console.error('Error moving chapter to part:', error);
      toast.error('Erro ao mover capítulo');
      fetchBookData();
    } finally {
      setDraggedChapter(null);
    }
  };

  // Handle dropping a chapter directly on a Part header (adds to end of that part)
  const handleDropOnPart = async (e, targetPartId, position = 0) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);
    
    if (!draggedChapter) {
      return;
    }
    
    // If already in this part, do nothing
    if (draggedChapter.part_id === targetPartId) {
      setDraggedChapter(null);
      return;
    }

    try {
      // Create a copy of chapters array
      const updatedChapters = [...chapters];
      
      // Find and remove dragged chapter
      const draggedIndex = updatedChapters.findIndex(c => c.chapter_id === draggedChapter.chapter_id);
      if (draggedIndex === -1) {
        setDraggedChapter(null);
        return;
      }
      
      const [movedChapter] = updatedChapters.splice(draggedIndex, 1);
      
      // Update part_id
      movedChapter.part_id = targetPartId;
      
      // Find position to insert (after last chapter in this part)
      const partChapters = updatedChapters.filter(ch => ch.part_id === targetPartId);
      let insertIndex;
      
      if (partChapters.length === 0) {
        // No chapters in part, find the part's position and insert after it
        const partIndex = parts.findIndex(p => p.part_id === targetPartId);
        // Insert at beginning or after previous part's chapters
        const previousPartsChapters = updatedChapters.filter(ch => {
          const chPartIndex = parts.findIndex(p => p.part_id === ch.part_id);
          return chPartIndex < partIndex;
        });
        insertIndex = previousPartsChapters.length;
      } else {
        // Insert after last chapter in part
        const lastPartChapter = partChapters[partChapters.length - 1];
        insertIndex = updatedChapters.findIndex(c => c.chapter_id === lastPartChapter.chapter_id) + 1;
      }
      
      // Insert chapter at new position
      updatedChapters.splice(insertIndex, 0, movedChapter);
      
      // Prepare bulk update data
      const reorderData = updatedChapters.map((ch, idx) => ({
        chapter_id: ch.chapter_id,
        order: idx + 1,
        part_id: ch.part_id
      }));

      // Send bulk reorder request
      await axios.put(
        `${API_URL}/api/books/${bookId}/chapters/reorder`,
        reorderData,
        { withCredentials: true }
      );

      // Update local state
      setChapters(updatedChapters);
      toast.success('Capítulo movido para a parte!');
    } catch (error) {
      console.error('Error moving chapter to part:', error);
      toast.error('Erro ao mover capítulo');
      fetchBookData();
    } finally {
      setDraggedChapter(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex-1 md:ml-64 p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex-1 md:ml-64 p-8">
          <p className="text-muted-foreground">Livro não encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 pt-14 md:pt-0" data-testid="livro-estrutura-page">
        {/* Book Header */}
        <div className="border-b border-border/40 p-4 md:p-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/meus-livros')}
            className="mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2" size={18} />
            Meus Livros
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif mb-1">{book.title}</h1>
              {book.subtitle && (
                <p className="text-lg text-muted-foreground mb-1">{book.subtitle}</p>
              )}
              <p className="text-muted-foreground">por {book.author}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6 max-w-4xl">
          
          {/* ✍️ ESCREVER O LIVRO - Big Button */}
          {/* Botão ESCREVER O LIVRO com barra de progresso integrada */}
          <div className="mb-8">
            <button
              onClick={() => {
                // Navigate to first chapter editor or last edited
                if (chapters.length > 0) {
                  const lastChapter = localStorage.getItem(`lastChapter_${bookId}`);
                  const targetChapter = lastChapter && chapters.find(ch => ch.chapter_id === lastChapter) 
                    ? lastChapter 
                    : chapters[0].chapter_id;
                  navigate(`/editor/${targetChapter}`);
                } else {
                  toast.info('Crie um capítulo primeiro para começar a escrever');
                }
              }}
              className="relative w-full overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.005]"
              data-testid="write-book-button"
            >
              {/* Background with progress gradient */}
              <div className="absolute inset-0" style={{ backgroundColor: '#f5f0e6' }}>
                <div 
                  className="h-full transition-all duration-700 ease-out"
                  style={{
                    width: `${progress.percentage}%`,
                    background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
                  }}
                />
              </div>
              
              {/* Content overlay */}
              <div className="relative p-6">
                <div className="flex items-center justify-center gap-4">
                  <PenTool size={32} className="text-stone-900" />
                  <div className="text-left">
                    <p className="text-2xl font-serif font-bold text-stone-900">ESCREVER O LIVRO</p>
                    <p className="text-sm text-stone-700">
                      {progress.percentage > 0 ? (
                        <>
                          <span className="font-semibold">{progress.percentage}%</span>
                          {' • '}
                          {progress.label}
                          {progress.isEstimate && (
                            <span className="opacity-70"> (estimativa)</span>
                          )}
                        </>
                      ) : (
                        'Abra o editor e dê vida à sua história'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* 🤝 COLEGAS ESCRITORES - Compacto e discreto */}
          <div className="mb-4 px-3 py-2 bg-muted/30 border border-border/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground">Autores:</span>
                
                {/* Owner - sempre visível */}
                {colleagues.owner && (
                  <span className="inline-flex items-center gap-1 text-sm">
                    <Crown size={12} className="text-amber-500" />
                    <span className="font-medium">{colleagues.owner.name}</span>
                  </span>
                )}
                
                {/* Colleagues */}
                {colleagues.colleagues.map((colleague, idx) => (
                  <span key={colleague.user_id} className="inline-flex items-center gap-1 text-sm group">
                    <span className="text-muted-foreground">•</span>
                    <span>{colleague.name || colleague.email.split('@')[0]}</span>
                    {colleagues.is_owner && (
                      <button
                        onClick={() => handleRemoveColleague(colleague.user_id)}
                        className="text-muted-foreground hover:text-destructive ml-0.5"
                        title="Remover colega"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </span>
                ))}
                
                {/* Pending invites */}
                {colleagues.pending_invites.length > 0 && colleagues.is_owner && (
                  <button
                    onClick={handleOpenInviteModal}
                    className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700"
                  >
                    <Mail size={12} />
                    <span>{colleagues.pending_invites.length} pendente(s)</span>
                  </button>
                )}
              </div>
              
              {/* Convidar button */}
              {colleagues.is_owner && (
                <button
                  onClick={handleOpenInviteModal}
                  className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
                >
                  <UserPlus size={12} />
                  <span>Convidar</span>
                </button>
              )}
            </div>
          </div>

          {/* 📘 IDENTIDADE DO LIVRO - Read Only */}
          <div className="mb-6">
            <button
              onClick={() => toggleSection('identidade')}
              className="flex items-center gap-2 w-full text-left py-3 hover:text-amber-600 transition-colors"
              data-testid="toggle-identidade-section"
            >
              {expandedSections.identidade ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className="text-xl">📘</span>
              <span className="font-serif text-lg">Identidade do Livro</span>
              <span className="text-xs text-muted-foreground ml-2">(visualização)</span>
            </button>
            
            {expandedSections.identidade && (
              <div className="ml-7 space-y-3">
                <div className="bg-card border border-border/40 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Clean 3D Book Mockup */}
                    <div className="flex gap-4 items-start">
                      <div className="shrink-0" style={{ perspective: '600px' }}>
                        <div 
                          className="relative"
                          style={{ 
                            transformStyle: 'preserve-3d',
                            transform: 'rotateY(-12deg)',
                            width: '90px',
                            height: '130px'
                          }}
                        >
                          {/* Spine */}
                          <div 
                            style={{
                              position: 'absolute',
                              top: '0',
                              left: '0',
                              width: '10px',
                              height: '100%',
                              background: 'linear-gradient(to right, #2a3a4a, #3a4a5a, #2a3a4a)',
                              transform: 'rotateY(90deg) translateX(-5px)',
                              transformOrigin: 'left center',
                              borderRadius: '2px 0 0 2px'
                            }}
                          />
                          {/* Pages */}
                          <div 
                            style={{
                              position: 'absolute',
                              top: '2px',
                              right: '-5px',
                              width: '10px',
                              height: 'calc(100% - 4px)',
                              background: 'linear-gradient(to right, #f8f8f5, #fff)',
                              borderRadius: '0 1px 1px 0'
                            }}
                          />
                          {/* Front */}
                          <div 
                            style={{ 
                              position: 'absolute',
                              top: '0',
                              left: '0',
                              width: '100%',
                              height: '100%',
                              borderRadius: '0 2px 2px 0',
                              overflow: 'hidden',
                              boxShadow: '3px 3px 12px rgba(0,0,0,0.15)'
                            }}
                          >
                            {book.cover_url ? (
                              <img src={book.cover_url} alt="Capa" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
                                <BookOpen className="w-5 h-5 text-stone-300" />
                              </div>
                            )}
                            <div style={{ position: 'absolute', inset: '0', pointerEvents: 'none', background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)' }} />
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-muted-foreground">Título</p>
                          <p className="font-medium">{book.title}</p>
                        </div>
                        {book.subtitle && (
                          <div>
                            <p className="text-xs text-muted-foreground">Subtítulo</p>
                            <p className="text-sm">{book.subtitle}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-muted-foreground">Autor(es)</p>
                          <p className="text-sm">{book.author}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Formato</p>
                        <p className="text-sm">{book.size === 'pocket' ? 'Pocket (12 x 18 cm)' : 'Kindle / Padrão (14 x 21 cm)'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Palavras</p>
                        <p className="text-sm">{(book.total_words || 0).toLocaleString('pt-BR')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Capítulos</p>
                        <p className="text-sm">{book.chapters_count || 0}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Edit button */}
                  <div className="mt-4 pt-4 border-t border-border/40">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/editar-livro/${bookId}`)}
                      className="text-amber-600 border-amber-600/30 hover:bg-amber-500/10"
                    >
                      <Edit size={14} className="mr-1" />
                      Editar Identidade
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 🧠 PLANEJAMENTO DO LIVRO */}
          <div className="mb-6">
            <button
              onClick={() => toggleSection('planejamento')}
              className="flex items-center gap-2 w-full text-left py-3 hover:text-amber-600 transition-colors"
              data-testid="toggle-planejamento-section"
            >
              {expandedSections.planejamento ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className="text-xl">🧠</span>
              <span className="font-serif text-lg">Planejamento do Livro</span>
            </button>
            
            {expandedSections.planejamento && (
              <div className="ml-7 space-y-3">
                <p className="text-sm text-muted-foreground mb-4">
                  Organize suas ideias antes de escrever. Marque como concluído conforme avançar.
                </p>
                
                {defaultPlanningItems.map((item) => (
                  <div key={item.key} className="bg-card border border-border/40 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => handlePlanningToggle(item.key)}
                        className="mt-1 shrink-0"
                      >
                        {planningData[item.key]?.completed ? (
                          <CheckSquare size={20} className="text-green-600" />
                        ) : (
                          <Square size={20} className="text-muted-foreground hover:text-amber-600" />
                        )}
                      </button>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`font-medium ${planningData[item.key]?.completed ? 'line-through text-muted-foreground' : ''}`}>
                            {item.label}
                          </span>
                          {editingField !== item.key && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingField(item.key)}
                            >
                              <Edit size={14} />
                            </Button>
                          )}
                        </div>
                        
                        {editingField === item.key ? (
                          <div className="space-y-2">
                            {item.type === 'number' ? (
                              <Input
                                type="number"
                                value={planningData[item.key]?.text || ''}
                                onChange={(e) => handlePlanningTextChange(item.key, e.target.value)}
                                placeholder={item.placeholder}
                                autoFocus
                                min="1"
                                className="w-32"
                              />
                            ) : (
                              <Textarea
                                value={planningData[item.key]?.text || ''}
                                onChange={(e) => handlePlanningTextChange(item.key, e.target.value)}
                                placeholder={item.placeholder}
                                rows={3}
                                autoFocus
                              />
                            )}
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handlePlanningTextSave(item.key)}
                                className="bg-amber-500 hover:bg-amber-600 text-stone-900"
                              >
                                <Save size={14} className="mr-1" /> Salvar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingField(null)}
                              >
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {planningData[item.key]?.text || item.placeholder}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 📚 ESTRUTURA DO LIVRO (SUMÁRIO) */}
          <div className="mb-6">
            <button
              onClick={() => toggleSection('sumario')}
              className="flex items-center gap-2 w-full text-left py-3 hover:text-amber-600 transition-colors"
              data-testid="toggle-sumario-section"
            >
              {expandedSections.sumario ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className="text-xl">📚</span>
              <span className="font-serif text-lg">Estrutura do Livro (Sumário)</span>
            </button>
            
            {expandedSections.sumario && (
              <div className="ml-7">
                
                {/* Placeholder example - only show if no content */}
                {parts.length === 0 && chapters.length === 0 && (
                  <div className="bg-muted/30 border border-dashed border-border rounded-lg p-6 mb-4">
                    <p className="font-medium mb-3 text-muted-foreground">Construa o Sumário do seu livro:</p>
                    <div className="space-y-1 text-sm text-muted-foreground/60 italic font-serif">
                      <p>PARTE I — O Começo</p>
                      <p className="ml-6">Capítulo 1 — O primeiro sinal</p>
                      <p className="ml-12">1.1 O encontro</p>
                      <p className="ml-12">1.2 A dúvida</p>
                      <p className="ml-6">Capítulo 2 — A descoberta</p>
                    </div>
                  </div>
                )}
                
                {/* Sumário Content - Visual like a real TOC */}
                <div className="bg-card border border-border/40 rounded-lg p-6 font-serif">
                  
                  {/* Parts with chapters */}
                  {parts.map((part, partIndex) => {
                    const partChapters = chapters.filter(ch => ch.part_id === part.part_id);
                    const isPartDragTarget = dragOverTarget?.partId === part.part_id && !dragOverTarget?.chapterId;
                    
                    return (
                      <div key={part.part_id} className="mb-6">
                        {/* Part header - can receive dropped chapters */}
                        <div
                          onDragOver={(e) => {
                            e.preventDefault();
                            if (draggedChapter && draggedChapter.part_id !== part.part_id) {
                              setDragOverTarget({ partId: part.part_id, chapterId: null });
                            }
                          }}
                          onDragLeave={() => setDragOverTarget(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (draggedChapter && draggedChapter.part_id !== part.part_id) {
                              handleMoveChapterToPart(draggedChapter, part.part_id);
                            }
                            setDragOverTarget(null);
                          }}
                          className={`rounded-lg transition-all ${isPartDragTarget ? 'bg-amber-100/50 ring-2 ring-amber-500 ring-offset-2' : ''}`}
                        >
                        {editingPartId === part.part_id ? (
                          <div className="flex items-center gap-2 mb-3 p-2">
                            <span className="text-amber-600 font-bold">PARTE {toRoman(partIndex + 1)} —</span>
                            <Input
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleEditPartTitle(part.part_id)}
                              autoFocus
                              className="flex-1 font-serif"
                            />
                            <Button size="sm" onClick={() => handleEditPartTitle(part.part_id)} className="bg-amber-500 hover:bg-amber-600 text-stone-900">
                              <Check size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={cancelEditing}>
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between group mb-3 p-2 -m-2">
                            <h3 className="text-lg font-bold text-amber-600 tracking-wide">
                              PARTE {toRoman(partIndex + 1)}{(() => {
                                // Evita duplicação como "PARTE I — Parte I" ou "PARTE I — I"
                                if (!part.title) return '';
                                const title = part.title.trim();
                                const titleLower = title.toLowerCase();
                                const romanNum = toRoman(partIndex + 1);
                                // Se o título é apenas o número romano ou "Parte X"
                                if (titleLower === `parte ${romanNum.toLowerCase()}` || 
                                    titleLower === romanNum.toLowerCase() ||
                                    titleLower.startsWith('parte ') && titleLower.replace('parte ', '').trim() === romanNum.toLowerCase()) {
                                  return '';
                                }
                                // Se começa com "Parte" ou é só um número romano, não mostra
                                if (/^parte\s+[ivxlcdm]+$/i.test(title) || /^[ivxlcdm]+$/i.test(title)) {
                                  return '';
                                }
                                return ` — ${title}`;
                              })()}
                            </h3>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" onClick={() => startEditingPart(part)} title="Editar">
                                <Edit size={14} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeletePart(part.part_id)} className="text-destructive hover:text-destructive" title="Excluir">
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        )}
                        </div>
                        
                        {/* Chapters in this part */}
                        <div className="ml-6 space-y-2">
                          {partChapters.map((chapter) => {
                            const chapterSubchapters = subchapters[chapter.chapter_id] || [];
                            const isDragOver = dragOverTarget?.chapterId === chapter.chapter_id;
                            // Global chapter index (continuous numbering across all parts)
                            const globalChapterIndex = chapters.findIndex(c => c.chapter_id === chapter.chapter_id);
                            
                            return (
                              <div 
                                key={chapter.chapter_id}
                                draggable={editingChapterId !== chapter.chapter_id}
                                onDragStart={(e) => handleDragStart(e, chapter)}
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => handleDragOver(e, chapter.chapter_id, part.part_id)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, chapter, part.part_id)}
                                className={isDragOver ? 'border-b-2 border-amber-500' : ''}
                              >
                                {/* Chapter row */}
                                {editingChapterId === chapter.chapter_id ? (
                                  <div className="flex items-center gap-2 py-1">
                                    <span className="text-muted-foreground">Capítulo {globalChapterIndex + 1} —</span>
                                    <Input
                                      value={editTitle}
                                      onChange={(e) => setEditTitle(e.target.value)}
                                      onKeyPress={(e) => e.key === 'Enter' && handleEditChapterTitle(chapter.chapter_id)}
                                      autoFocus
                                      className="flex-1 font-serif"
                                    />
                                    <Button size="sm" onClick={() => handleEditChapterTitle(chapter.chapter_id)} className="bg-amber-500 hover:bg-amber-600 text-stone-900">
                                      <Check size={14} />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={cancelEditing}>
                                      <X size={14} />
                                    </Button>
                                  </div>
                                ) : (
                                  <div 
                                    className="flex items-center justify-between group py-2 md:py-1 hover:bg-muted/30 rounded px-2 -mx-2 cursor-grab active:cursor-grabbing"
                                    onClick={() => navigate(`/editor/${chapter.chapter_id}`)}
                                  >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <GripVertical size={14} className="text-muted-foreground/50 hidden md:block md:opacity-0 md:group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                      <p className="text-sm md:text-base truncate">
                                        <span className="text-muted-foreground">Cap. {globalChapterIndex + 1}</span> — {chapter.title}
                                      </p>
                                      {/* Synopsis indicator - visible on mobile */}
                                      {chapter.synopsis && (
                                        <PostItIcon size={14} hasContent={true} className="flex-shrink-0" />
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex-shrink-0">
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        onClick={(e) => { e.stopPropagation(); startEditingSynopsis(chapter); }} 
                                        title={chapter.synopsis ? "Editar síntese" : "Adicionar síntese"}
                                        className="h-7 w-7 p-0"
                                      >
                                        <PostItIcon size={16} hasContent={!!chapter.synopsis} />
                                      </Button>
                                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); startEditingChapter(chapter); }} title="Editar título" className="h-7 w-7 p-0">
                                        <Edit size={14} />
                                      </Button>
                                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteChapter(chapter.chapter_id); }} className="text-destructive hover:text-destructive h-7 w-7 p-0" title="Excluir">
                                        <Trash2 size={14} />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Synopsis editing modal */}
                                {editingSynopsisId === chapter.chapter_id && (
                                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center gap-2 mb-2">
                                      <PostItIcon size={18} hasContent={true} />
                                      <span className="text-sm font-medium text-amber-800">Síntese do Capítulo</span>
                                    </div>
                                    <Textarea
                                      value={synopsisText}
                                      onChange={(e) => setSynopsisText(e.target.value)}
                                      placeholder="O que acontece neste capítulo? Anote aqui para não se perder..."
                                      className="min-h-[80px] text-sm bg-white border-amber-200 focus:border-amber-400"
                                      autoFocus
                                    />
                                    <div className="flex justify-end gap-2 mt-2">
                                      <Button variant="ghost" size="sm" onClick={cancelSynopsisEditing}>
                                        Cancelar
                                      </Button>
                                      <Button size="sm" onClick={() => handleSaveSynopsis(chapter.chapter_id)} className="bg-amber-500 hover:bg-amber-600 text-stone-900">
                                        <Check size={14} className="mr-1" />
                                        Salvar
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Subchapters - Only show if there are subchapters */}
                                {chapterSubchapters.length > 0 && (
                                  <div className="ml-6 space-y-1">
                                    {chapterSubchapters.map((subch, subIndex) => (
                                      editingSubchapterId === subch.subchapter_id ? (
                                        <div key={subch.subchapter_id} className="flex items-center gap-2 py-1">
                                          <span className="text-muted-foreground">{globalChapterIndex + 1}.{subIndex + 1}</span>
                                          <Input
                                            value={editTitle}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleEditSubchapterTitle(subch.subchapter_id)}
                                            autoFocus
                                            className="flex-1 font-serif text-sm"
                                          />
                                          <Button size="sm" onClick={() => handleEditSubchapterTitle(subch.subchapter_id)} className="bg-amber-500 hover:bg-amber-600 text-stone-900">
                                            <Check size={14} />
                                          </Button>
                                          <Button variant="ghost" size="sm" onClick={cancelEditing}>
                                            <X size={14} />
                                          </Button>
                                        </div>
                                      ) : (
                                        <div key={subch.subchapter_id} className="flex items-center justify-between group py-1 hover:bg-muted/30 rounded px-2 -mx-2">
                                          <p className="text-sm text-muted-foreground">
                                            {globalChapterIndex + 1}.{subIndex + 1} {subch.title}
                                          </p>
                                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button variant="ghost" size="sm" onClick={() => startEditingSubchapter(subch)} title="Editar" className="h-6 w-6 p-0">
                                              <Edit size={12} />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDeleteSubchapter(subch.subchapter_id)} className="text-destructive hover:text-destructive h-6 w-6 p-0" title="Excluir">
                                              <Trash2 size={12} />
                                            </Button>
                                          </div>
                                        </div>
                                      )
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Standalone chapters (no part) - show when there are no parts OR there are orphan chapters */}
                  {(parts.length === 0 || chapters.some(ch => !ch.part_id)) && (
                    <div className="space-y-2">
                      {parts.length > 0 && chapters.some(ch => !ch.part_id) && (
                        <h4 className="text-sm text-muted-foreground mt-4 mb-2 font-medium">Capítulos sem parte</h4>
                      )}
                      {chapters
                        .filter(ch => parts.length === 0 || !ch.part_id)
                        .map((chapter, index) => {
                          // Global chapter index (continuous numbering)
                          const globalChapterIndex = chapters.findIndex(c => c.chapter_id === chapter.chapter_id);
                          const chapterSubchapters = subchapters[chapter.chapter_id] || [];
                          const isDragOver = dragOverTarget?.chapterId === chapter.chapter_id;
                        
                          return (
                            <div 
                              key={chapter.chapter_id}
                              draggable={editingChapterId !== chapter.chapter_id}
                              onDragStart={(e) => handleDragStart(e, chapter)}
                              onDragEnd={handleDragEnd}
                              onDragOver={(e) => handleDragOver(e, chapter.chapter_id, null)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, chapter, null)}
                              className={isDragOver ? 'border-b-2 border-amber-500' : ''}
                            >
                              {/* Chapter row */}
                              {editingChapterId === chapter.chapter_id ? (
                                <div className="flex items-center gap-2 py-1">
                                  <span className="text-muted-foreground">Capítulo {globalChapterIndex + 1} —</span>
                                  <Input
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleEditChapterTitle(chapter.chapter_id)}
                                    autoFocus
                                    className="flex-1 font-serif"
                                  />
                                  <Button size="sm" onClick={() => handleEditChapterTitle(chapter.chapter_id)} className="bg-amber-500 hover:bg-amber-600 text-stone-900">
                                    <Check size={14} />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={cancelEditing}>
                                    <X size={14} />
                                  </Button>
                                </div>
                              ) : (
                                <div 
                                  className="flex items-center justify-between group py-2 md:py-1 hover:bg-muted/30 rounded px-2 -mx-2 cursor-grab active:cursor-grabbing"
                                  onClick={() => navigate(`/editor/${chapter.chapter_id}`)}
                                >
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <GripVertical size={14} className="text-muted-foreground/50 hidden md:block md:opacity-0 md:group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                    <p className="text-sm md:text-base truncate">
                                      <span className="text-muted-foreground">Cap. {globalChapterIndex + 1}</span> — {chapter.title}
                                    </p>
                                    {/* Synopsis indicator - visible on mobile */}
                                    {chapter.synopsis && (
                                      <PostItIcon size={14} hasContent={true} className="flex-shrink-0" />
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex-shrink-0">
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={(e) => { e.stopPropagation(); startEditingSynopsis(chapter); }} 
                                      title={chapter.synopsis ? "Editar síntese" : "Adicionar síntese"}
                                      className="h-7 w-7 p-0"
                                    >
                                      <PostItIcon size={16} hasContent={!!chapter.synopsis} />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); startEditingChapter(chapter); }} title="Editar título" className="h-7 w-7 p-0">
                                      <Edit size={14} />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteChapter(chapter.chapter_id); }} className="text-destructive hover:text-destructive h-7 w-7 p-0" title="Excluir">
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                </div>
                              )}
                              
                              {/* Synopsis editing modal */}
                              {editingSynopsisId === chapter.chapter_id && (
                                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <PostItIcon size={18} hasContent={true} />
                                    <span className="text-sm font-medium text-amber-800">Síntese do Capítulo</span>
                                  </div>
                                  <Textarea
                                    value={synopsisText}
                                    onChange={(e) => setSynopsisText(e.target.value)}
                                    placeholder="O que acontece neste capítulo? Anote aqui para não se perder..."
                                    className="min-h-[80px] text-sm bg-white border-amber-200 focus:border-amber-400"
                                    autoFocus
                                  />
                                  <div className="flex justify-end gap-2 mt-2">
                                    <Button variant="ghost" size="sm" onClick={cancelSynopsisEditing}>
                                      Cancelar
                                    </Button>
                                    <Button size="sm" onClick={() => handleSaveSynopsis(chapter.chapter_id)} className="bg-amber-500 hover:bg-amber-600 text-stone-900">
                                      <Check size={14} className="mr-1" />
                                      Salvar
                                    </Button>
                                  </div>
                                </div>
                              )}
                              
                              {/* Subchapters - Only show if there are subchapters */}
                              {chapterSubchapters.length > 0 && (
                                <div className="ml-6 space-y-1">
                                  {chapterSubchapters.map((subch, subIndex) => (
                                    editingSubchapterId === subch.subchapter_id ? (
                                      <div key={subch.subchapter_id} className="flex items-center gap-2 py-1">
                                        <span className="text-muted-foreground">{globalChapterIndex + 1}.{subIndex + 1}</span>
                                        <Input
                                          value={editTitle}
                                          onChange={(e) => setEditTitle(e.target.value)}
                                          onKeyPress={(e) => e.key === 'Enter' && handleEditSubchapterTitle(subch.subchapter_id)}
                                          autoFocus
                                          className="flex-1 font-serif text-sm"
                                        />
                                        <Button size="sm" onClick={() => handleEditSubchapterTitle(subch.subchapter_id)} className="bg-amber-500 hover:bg-amber-600 text-stone-900">
                                          <Check size={14} />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={cancelEditing}>
                                          <X size={14} />
                                        </Button>
                                      </div>
                                    ) : (
                                      <div key={subch.subchapter_id} className="flex items-center justify-between group py-1 hover:bg-muted/30 rounded px-2 -mx-2">
                                        <p className="text-sm text-muted-foreground">
                                          {globalChapterIndex + 1}.{subIndex + 1} {subch.title}
                                        </p>
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <Button variant="ghost" size="sm" onClick={() => startEditingSubchapter(subch)} title="Editar" className="h-6 w-6 p-0">
                                            <Edit size={12} />
                                          </Button>
                                          <Button variant="ghost" size="sm" onClick={() => handleDeleteSubchapter(subch.subchapter_id)} className="text-destructive hover:text-destructive h-6 w-6 p-0" title="Excluir">
                                            <Trash2 size={12} />
                                          </Button>
                                        </div>
                                      </div>
                                    )
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                  
                  {/* Action Buttons - Below the sumário */}
                  <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-border/40">
                    {/* Add Part */}
                    {addingPart ? (
                      <div className="flex gap-2">
                        <Input
                          value={newPartTitle}
                          onChange={(e) => setNewPartTitle(e.target.value)}
                          placeholder="Título da parte"
                          onKeyPress={(e) => e.key === 'Enter' && handleCreatePart()}
                          autoFocus
                          className="w-48 font-serif"
                        />
                        <Button size="sm" onClick={handleCreatePart} className="bg-amber-500 hover:bg-amber-600 text-stone-900">
                          <Check size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setAddingPart(false)}>
                          <X size={14} />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAddingPart(true)}
                        data-testid="add-part-btn"
                      >
                        <Plus size={16} className="mr-1" />
                        Parte
                      </Button>
                    )}
                    
                    {/* Add Chapter */}
                    {addingChapter === 'selecting' ? (
                      // Show part selection dropdown
                      <div className="flex gap-2 items-center">
                        <select
                          className="px-3 py-1.5 border rounded-md text-sm font-serif bg-background"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value) setAddingChapter(value === 'standalone' ? 'standalone' : value);
                          }}
                          autoFocus
                        >
                          <option value="">Onde adicionar?</option>
                          <option value="standalone">Capítulo sem parte</option>
                          {parts.map((part, idx) => (
                            <option key={part.part_id} value={part.part_id}>
                              Parte {toRoman(idx + 1)}{part.title && part.title !== `Parte ${toRoman(idx + 1)}` ? ` — ${part.title}` : ''}
                            </option>
                          ))}
                        </select>
                        <Button variant="ghost" size="sm" onClick={() => setAddingChapter(null)}>
                          <X size={14} />
                        </Button>
                      </div>
                    ) : addingChapter && addingChapter !== 'selecting' ? (
                      // Show chapter title input
                      <div className="flex gap-2">
                        <Input
                          value={newChapterTitle}
                          onChange={(e) => setNewChapterTitle(e.target.value)}
                          placeholder="Título do capítulo"
                          onKeyPress={(e) => e.key === 'Enter' && handleCreateChapter(addingChapter === 'standalone' ? null : addingChapter)}
                          autoFocus
                          className="w-48 font-serif"
                        />
                        <Button size="sm" onClick={() => handleCreateChapter(addingChapter === 'standalone' ? null : addingChapter)} className="bg-amber-500 hover:bg-amber-600 text-stone-900">
                          <Check size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setAddingChapter(null)}>
                          <X size={14} />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // If no parts, go directly to standalone
                          // Otherwise, always show selection (including "sem parte" option)
                          if (parts.length === 0) {
                            setAddingChapter('standalone');
                          } else {
                            setAddingChapter('selecting');
                          }
                        }}
                        data-testid="add-chapter-btn"
                      >
                        <Plus size={16} className="mr-1" />
                        Capítulo
                      </Button>
                    )}
                    
                    {/* Add Subchapter - Only show if there are chapters */}
                    {chapters.length > 0 && (
                      addingSubchapter === 'selecting' ? (
                        // Show chapter selection dropdown
                        <div className="flex gap-2 items-center">
                          <select
                            className="px-3 py-1.5 border rounded-md text-sm font-serif bg-background"
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value) setAddingSubchapter(value);
                            }}
                            autoFocus
                          >
                            <option value="">Em qual capítulo?</option>
                            {chapters.map((ch, idx) => (
                              <option key={ch.chapter_id} value={ch.chapter_id}>
                                Capítulo {idx + 1} — {ch.title}
                              </option>
                            ))}
                          </select>
                          <Button variant="ghost" size="sm" onClick={() => setAddingSubchapter(null)}>
                            <X size={14} />
                          </Button>
                        </div>
                      ) : addingSubchapter && addingSubchapter !== 'selecting' ? (
                        // Show subchapter title input
                        <div className="flex gap-2">
                          <Input
                            value={newSubchapterTitle}
                            onChange={(e) => setNewSubchapterTitle(e.target.value)}
                            placeholder="Título do subcapítulo"
                            onKeyPress={(e) => e.key === 'Enter' && handleCreateSubchapterWithTitle(addingSubchapter)}
                            autoFocus
                            className="w-48 font-serif"
                          />
                          <Button size="sm" onClick={() => handleCreateSubchapterWithTitle(addingSubchapter)} className="bg-amber-500 hover:bg-amber-600 text-stone-900">
                            <Check size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setAddingSubchapter(null)}>
                            <X size={14} />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // If only one chapter, go directly to it
                            // Otherwise, show selection
                            if (chapters.length === 1) {
                              setAddingSubchapter(chapters[0].chapter_id);
                            } else {
                              setAddingSubchapter('selecting');
                            }
                          }}
                          data-testid="add-subchapter-btn"
                        >
                          <Plus size={16} className="mr-1" />
                          Subcapítulo
                        </Button>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 👥 PARTICIPAÇÃO DOS AUTORES */}
          <div className="mb-6">
            <button
              onClick={() => toggleSection('autoria')}
              className="flex items-center gap-2 w-full text-left py-3 hover:text-amber-600 transition-colors"
              data-testid="toggle-autoria-section"
            >
              {expandedSections.autoria ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className="text-xl">👥</span>
              <span className="font-serif text-lg">Participação dos Autores</span>
            </button>
            
            {expandedSections.autoria && (
              <div className="ml-7 space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Acompanhe a contribuição de cada autor na obra. O sistema registra automaticamente as palavras escritas por cada um.
                </p>
                
                {/* Authorship Panel */}
                <AuthorshipPanel 
                  bookId={bookId} 
                  bookTitle={book?.title}
                />
                
                {/* Certificate Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={() => setShowCertificateModal(true)}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black"
                    data-testid="generate-certificate-btn"
                  >
                    <Award size={16} className="mr-2" />
                    Gerar Certificado de Autoria
                  </Button>
                </div>

                {/* Diagnostic Tool - only for book owner */}
                <div className="mt-6 pt-6 border-t border-white/10">
                  <CoauthorDiagnostic 
                    bookId={bookId}
                    bookTitle={book?.title}
                    onFixed={() => window.location.reload()}
                  />
                </div>
              </div>
            )}
          </div>

          {/* 🙏 ENCERRAMENTO DO LIVRO */}
          <div className="mb-6">
            <button
              onClick={() => toggleSection('encerramento')}
              className="flex items-center gap-2 w-full text-left py-3 hover:text-amber-600 transition-colors"
              data-testid="toggle-encerramento-section"
            >
              {expandedSections.encerramento ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className="text-xl">🙏</span>
              <span className="font-serif text-lg">Encerramento do Livro</span>
            </button>
            
            {expandedSections.encerramento && (
              <div className="ml-7 space-y-3">
                <p className="text-sm text-muted-foreground mb-4">
                  Textos finais do livro, geralmente escritos quando a obra está avançada ou finalizada.
                </p>
                
                {/* Dedicatória */}
                <EncerramentoField
                  label="Dedicatória"
                  icon={FileText}
                  fieldName="dedication"
                  value={encerramentoData.dedication}
                  onChange={(val) => setEncerramentoData(prev => ({ ...prev, dedication: val }))}
                  isEditing={editingField === 'dedication'}
                  onEdit={() => setEditingField('dedication')}
                  onSave={() => handleSaveEncerramentoField('dedication')}
                  onCancel={() => setEditingField(null)}
                  saving={savingField === 'dedication'}
                  placeholder="Para quem você dedica esta obra?"
                />
                
                {/* Prefácio */}
                <EncerramentoField
                  label="Prefácio"
                  icon={BookOpen}
                  fieldName="preface"
                  value={encerramentoData.preface}
                  onChange={(val) => setEncerramentoData(prev => ({ ...prev, preface: val }))}
                  isEditing={editingField === 'preface'}
                  onEdit={() => setEditingField('preface')}
                  onSave={() => handleSaveEncerramentoField('preface')}
                  onCancel={() => setEditingField(null)}
                  saving={savingField === 'preface'}
                  placeholder="Uma introdução para o leitor..."
                />
                
                {/* Agradecimentos */}
                <EncerramentoField
                  label="Agradecimentos"
                  icon={Users}
                  fieldName="acknowledgments"
                  value={encerramentoData.acknowledgments}
                  onChange={(val) => setEncerramentoData(prev => ({ ...prev, acknowledgments: val }))}
                  isEditing={editingField === 'acknowledgments'}
                  onEdit={() => setEditingField('acknowledgments')}
                  onSave={() => handleSaveEncerramentoField('acknowledgments')}
                  onCancel={() => setEditingField(null)}
                  saving={savingField === 'acknowledgments'}
                  placeholder="Agradeça quem fez parte desta jornada"
                />
                
                {/* Sobre o Autor */}
                <EncerramentoField
                  label="Sobre o Autor"
                  icon={BookMarked}
                  fieldName="about_author"
                  value={encerramentoData.about_author}
                  onChange={(val) => setEncerramentoData(prev => ({ ...prev, about_author: val }))}
                  isEditing={editingField === 'about_author'}
                  onEdit={() => setEditingField('about_author')}
                  onSave={() => handleSaveEncerramentoField('about_author')}
                  onCancel={() => setEditingField(null)}
                  saving={savingField === 'about_author'}
                  placeholder="Conte um pouco sobre você como autor"
                />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-serif">Colegas Escritores</h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowInviteModal(false); setInviteLink(null); setInviteEmail(''); }}>
                <X size={18} />
              </Button>
            </div>
            
            {/* Pending Invites Section */}
            {colleagues.pending_invites.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Mail size={14} />
                  Convites Pendentes
                </h4>
                <div className="space-y-2">
                  {colleagues.pending_invites.map((invite) => (
                    <div key={invite.invite_id || invite.invited_email} className="flex items-center justify-between p-2 bg-muted/30 rounded-md text-sm">
                      <span className="truncate flex-1">{invite.invited_email}</span>
                      <button
                        onClick={async () => {
                          try {
                            await axios.delete(`${API_URL}/api/books/${bookId}/invites/${invite.invite_id}`, { withCredentials: true });
                            toast.success('Convite cancelado');
                            fetchColleagues();
                          } catch (error) {
                            toast.error('Erro ao cancelar convite');
                          }
                        }}
                        className="text-muted-foreground hover:text-destructive ml-2"
                        title="Cancelar convite"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* New Invite Form */}
            {!inviteLink ? (
              <>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <UserPlus size={14} />
                  Novo Convite
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  O convite é vinculado ao email. Apenas essa pessoa poderá aceitar.
                </p>
                
                <div className="space-y-3">
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="email@exemplo.com"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendInvite()}
                  />
                  
                  <Button
                    onClick={handleSendInvite}
                    disabled={sendingInvite}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900"
                    size="sm"
                  >
                    {sendingInvite ? 'Criando...' : 'Criar convite'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 mb-3">
                  <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                    Convite criado!
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Envie para <strong>{inviteEmail}</strong>
                  </p>
                </div>
                
                <div className="flex gap-2 mb-3">
                  <Input
                    value={inviteLink}
                    readOnly
                    className="text-xs"
                  />
                  <Button onClick={copyInviteLink} variant="outline" size="sm">
                    <Copy size={14} />
                  </Button>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => { setInviteLink(null); setInviteEmail(''); }}
                  >
                    Convidar outro
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-stone-900"
                    onClick={() => { setShowInviteModal(false); setInviteLink(null); setInviteEmail(''); }}
                  >
                    Fechar
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Certificate Modal */}
      {showCertificateModal && (
        <AuthorshipCertificate
          bookId={bookId}
          bookTitle={book?.title}
          onClose={() => setShowCertificateModal(false)}
        />
      )}
    </div>
  );
}

// Encerramento Field Component
function EncerramentoField({ label, icon: Icon, fieldName, value, onChange, isEditing, onEdit, onSave, onCancel, saving, placeholder }) {
  return (
    <div className="bg-card border border-border/40 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Icon size={18} className="text-amber-500" />
          <span className="font-medium">{label}</span>
        </div>
        {!isEditing && (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Edit size={16} />
          </Button>
        )}
      </div>
      
      {isEditing ? (
        <div className="space-y-3">
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            placeholder={placeholder}
            autoFocus
          />
          <div className="flex gap-2">
            <Button 
              size="sm" 
              onClick={onSave} 
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-stone-900"
            >
              <Save size={14} className="mr-1" />
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">
          {value || `Clique em editar para adicionar ${label.toLowerCase()}...`}
        </p>
      )}
    </div>
  );
}
